import { FormAccordion } from '@strata/react-form-section';
import { Spinner } from '@strata/react-loading-animation';
import { useFormikContext } from 'formik';
import { useMemo, useState } from 'react';

import { BillingAddressSection } from 'components/wonReferrals/claimSubmission/BillingAddressSection';
import { ClaimInformationSection } from 'components/wonReferrals/claimSubmission/ClaimInformationSection';
import { MedicalBillingAttestationDialog } from 'components/wonReferrals/claimSubmission/dialogs/MedicalBillingAttestationDialog';
import { type ClaimSubmissionFormSchema } from 'services/MedicalBillingConstants';
import {
    type DiagnosisCodeOption,
    type ProductCodeOption,
    type SupplierDetails,
    useGetSupplierDetailsQuery,
} from 'store/api/referenceApi';
import { type ReferralDetails } from 'store/api/treatmentApi';

interface ClaimSubmissionFormAccordionProps {
    isRevision?: boolean;
    referralDetails?: ReferralDetails;
    parentSupplierDetails?: SupplierDetails;
    diagnosesList?: DiagnosisCodeOption[];
    productCodeArrFetching?: boolean;
    productCodeDropdownOptions?: ProductCodeOption[];
}

export const ClaimSubmissionFormAccordion = ({
    isRevision = false,
    referralDetails = undefined,
    parentSupplierDetails = undefined,
    diagnosesList = undefined,
    productCodeArrFetching = true,
    productCodeDropdownOptions = undefined,
}: ClaimSubmissionFormAccordionProps) => {
    const formik = useFormikContext<ClaimSubmissionFormSchema>();

    const [showAttestationDialog, setShowAttestationDialog] = useState(false);

    const { data: childSupplierDetails } = useGetSupplierDetailsQuery({
        npi: formik.values.supplierNpi,
    });

    const [billingAddressSectionOpen, setBillingAddressSectionOpen] = useState(false);
    const [claimInfoSectionOpen, setClaimInfoSectionOpen] = useState(true);

    const sectionDataArray = useMemo(
        () => [
            {
                step: 1,
                sectionTitle: 'Pharmacy Billing Address',
                progressIndicatorStatus: FormAccordion.statuses.COMPLETED,
                onSectionClick: () => setBillingAddressSectionOpen((prev) => !prev),
                isOpen: billingAddressSectionOpen,
                displayFooter: false,
                disabled: false,
                content: <BillingAddressSection childSupplierDetails={childSupplierDetails} />,
            },
            {
                step: 2,
                sectionTitle: 'Claim Information',
                progressIndicatorStatus: FormAccordion.statuses.ACTIVE,
                onSectionClick: () => setClaimInfoSectionOpen((prev) => !prev),
                isOpen: claimInfoSectionOpen,
                displayFooter: true,
                disabled: false,
                content:
                    diagnosesList && !productCodeArrFetching ? (
                        <ClaimInformationSection
                            isRevision={isRevision}
                            referralDetails={referralDetails}
                            parentSupplierDetails={parentSupplierDetails}
                            childSupplierDetails={childSupplierDetails}
                            diagnosesList={diagnosesList}
                            productCodeDropdownOptions={productCodeDropdownOptions!}
                        />
                    ) : (
                        <div className="padding__left--small" style={{ display: 'flex' }}>
                            <Spinner />
                        </div>
                    ),
            },
        ],
        [
            productCodeArrFetching,
            billingAddressSectionOpen,
            claimInfoSectionOpen,
            childSupplierDetails,
            diagnosesList,
            isRevision,
            parentSupplierDetails,
            productCodeDropdownOptions,
            referralDetails,
        ],
    );

    return (
        <>
            <MedicalBillingAttestationDialog
                isOpen={showAttestationDialog}
                onPrimaryButtonClick={() => {
                    setShowAttestationDialog(false);
                    formik.handleSubmit();
                }}
                onSecondaryButtonClick={() => setShowAttestationDialog(false)}
            />
            <FormAccordion
                primaryButtonText={`Submit ${isRevision ? 'Revised ' : ''}Estimate`}
                secondaryButtonText="Cancel"
                onSubmitClick={() => setShowAttestationDialog(true)}
                onCancelClick={formik.handleReset}
                isSubmitting={!formik.isValid}
                sectionDataArray={sectionDataArray}
            />
        </>
    );
};
